import { z } from 'zod';
import { ImageVariantUrlsSchema } from './images.js';
import { InvoiceTaxTypeSchema } from './invoices.js';
import { LineItemUnitTypeSchema } from './line-items.js';
import { TagResponseSchema } from './tags.js';
import { optionalStr } from './utils.js';
// #region Enums
/** Whether an expense is a business deduction or a client reimbursement. */
export const ExpenseTypeSchema = z.enum(['BUSINESS', 'REIMBURSEMENT']);
/**
 * Whether the claimable amount is expressed as a dollar value or a percentage of total
 * cost.
 */
export const ClaimableTypeSchema = z.enum(['DOLLARS', 'PERCENTAGE']);
/**
 * Resolves an expense's claimable amount to a dollar value. When `claimableAmountType` is
 * `PERCENTAGE`, `claimableAmount` holds a raw percentage (e.g. `20` for 20%) rather than
 * a dollar figure, so it must be applied against `totalCost` first.
 */
export function resolveClaimableAmount(totalCost, claimableAmount, claimableAmountType) {
    if (claimableAmountType === 'PERCENTAGE')
        return (totalCost * claimableAmount) / 100;
    return claimableAmount;
}
/**
 * ATO expense category schedule. Members map to display labels in
 * {@link EXPENSE_CATEGORY_META}.
 */
export const ExpenseCategorySchema = z.enum([
    'ADVERTISING',
    'BANK_FEES',
    'COST_OF_GOODS_SOLD',
    'DEPRECIATION',
    'DIVIDENDS_DEDUCTION',
    'DONATIONS',
    'EQUIPMENT_PURCHASE',
    'HEALTH_AND_SAFETY_EQUIPMENT',
    'HOME_OFFICE_EXPENSES_ACTUAL',
    'HOME_OFFICE_EXPENSES_FIXED_RATE',
    'INTERNET',
    'MOBILE_PHONE_BILL',
    'MOTOR_VEHICLE_EXPENSES',
    'MOTOR_VEHICLE_EXPENSES_CENTS_PER_KM',
    'MOTOR_VEHICLE_PURCHASE',
    'PARKING_AND_TRANSPORT',
    'PAYROLL',
    'PRINTING_AND_STATIONERY',
    'PROFESSIONAL_FEES',
    'PROFESSIONAL_INSURANCE',
    'REFUNDS',
    'RENT_EXTERNAL_SPACE_HIRE',
    'REPAIRS_AND_MAINTENANCE_EQUIPMENT',
    'REPAIRS_AND_MAINTENANCE_HOME_OFFICE',
    'RESEARCH_AND_DEVELOPMENT',
    'SUB_CONTRACTOR_FEES',
    'SUBSCRIPTION_FEES',
    'SUPERANNUATION_CONTRIBUTION',
    'TRAVEL_NATIONAL_AND_INTERNATIONAL',
    'UNIFORM',
]);
// #endregion
// #region Category metadata
/** Display label and ATO schedule code for each {@link ExpenseCategory}. */
export const EXPENSE_CATEGORY_META = {
    ADVERTISING: { label: 'Advertising', code: 63 },
    BANK_FEES: { label: 'Bank Fees', code: 62 },
    COST_OF_GOODS_SOLD: { label: 'Cost of goods sold', code: 61 },
    DEPRECIATION: { label: 'Depreciation', code: 50 },
    DIVIDENDS_DEDUCTION: { label: 'Dividends deduction', code: 105 },
    DONATIONS: { label: 'Donations', code: 43 },
    EQUIPMENT_PURCHASE: { label: 'Equipment (Purchase)', code: 37 },
    HEALTH_AND_SAFETY_EQUIPMENT: { label: 'Health and Safety Equipment', code: 49 },
    HOME_OFFICE_EXPENSES_ACTUAL: { label: 'Home Office Expenses (Actual)', code: 41 },
    HOME_OFFICE_EXPENSES_FIXED_RATE: {
        label: 'Home Office Expenses (Fixed rate)',
        code: 60,
    },
    INTERNET: { label: 'Internet', code: 51 },
    MOBILE_PHONE_BILL: { label: 'Mobile Phone Bill', code: 58 },
    MOTOR_VEHICLE_EXPENSES: { label: 'Motor Vehicle Expenses', code: 25 },
    MOTOR_VEHICLE_EXPENSES_CENTS_PER_KM: {
        label: 'Motor Vehicle Expenses (cents per km)',
        code: 36,
    },
    MOTOR_VEHICLE_PURCHASE: { label: 'Motor Vehicle Purchase', code: 40 },
    PARKING_AND_TRANSPORT: { label: 'Parking and Transport', code: 24 },
    PAYROLL: { label: 'Payroll', code: 100 },
    PRINTING_AND_STATIONERY: { label: 'Printing & Stationery', code: 66 },
    PROFESSIONAL_FEES: { label: 'Professional Fees', code: 42 },
    PROFESSIONAL_INSURANCE: { label: 'Professional Insurance', code: 56 },
    REFUNDS: { label: 'Refunds', code: 55 },
    RENT_EXTERNAL_SPACE_HIRE: { label: 'Rent - External Space Hire', code: 57 },
    REPAIRS_AND_MAINTENANCE_EQUIPMENT: {
        label: 'Repairs and Maintenance - Equipment',
        code: 103,
    },
    REPAIRS_AND_MAINTENANCE_HOME_OFFICE: {
        label: 'Repairs and Maintenance - Home Office',
        code: 104,
    },
    RESEARCH_AND_DEVELOPMENT: { label: 'Research and Development', code: 54 },
    SUB_CONTRACTOR_FEES: { label: 'Sub-Contractor Fees', code: 44 },
    SUBSCRIPTION_FEES: { label: 'Subscription fees', code: 53 },
    SUPERANNUATION_CONTRIBUTION: { label: 'Superannuation Contribution', code: 20 },
    TRAVEL_NATIONAL_AND_INTERNATIONAL: {
        label: 'Travel - National and International',
        code: 52,
    },
    UNIFORM: { label: 'Uniform', code: 47 },
};
// #endregion
// #region Shared cost fields (used by both expense types)
const CostFieldsSchema = z.object({
    expenseDate: z.iso.datetime({ error: 'Expense date is required' }),
    totalCost: z.coerce
        .number({ error: 'Total cost must be a number' })
        .min(0, { error: 'Total cost must be 0 or greater' }),
    taxIncluded: z.boolean().default(false),
    taxType: InvoiceTaxTypeSchema.default('NONE'),
    taxRate: z.coerce
        .number()
        .min(0, { error: 'Tax rate cannot be negative' })
        .max(1, { error: 'Tax rate cannot exceed 100%' })
        .default(0),
    claimableAmountType: ClaimableTypeSchema,
    claimableAmount: z.coerce
        .number({ error: 'Claimable amount must be a number' })
        .min(0, { error: 'Claimable amount must be 0 or greater' }),
    title: optionalStr,
    description: optionalStr,
});
// #endregion
// #region Create schemas
/** Request body for creating a business expense. */
export const CreateBusinessExpenseSchema = CostFieldsSchema.extend({
    type: z.literal('BUSINESS'),
    category: ExpenseCategorySchema,
    lineItemId: optionalStr,
    claimed: z.boolean().optional(),
    tagIds: z.array(z.string()).optional(),
});
/** Request body for creating a client reimbursement expense. */
export const CreateReimbursementExpenseSchema = CostFieldsSchema.extend({
    type: z.literal('REIMBURSEMENT'),
    title: z.string().min(1, { error: 'Title is required' }),
    clientId: z.string().min(1, { error: 'Client is required' }),
    unitType: LineItemUnitTypeSchema.default('UNIT'),
    quantity: z.coerce
        .number({ error: 'Quantity must be a number' })
        .positive({ error: 'Quantity must be greater than 0' })
        .default(1),
    tagIds: z.array(z.string()).optional(),
});
/** Discriminated union over expense type — routes to the correct sub-schema. */
export const CreateExpenseSchema = z.discriminatedUnion('type', [
    CreateBusinessExpenseSchema,
    CreateReimbursementExpenseSchema,
]);
/** Partial update for a business expense — all fields optional except type. */
export const UpdateBusinessExpenseSchema = CreateBusinessExpenseSchema.partial().extend({
    type: z.literal('BUSINESS'),
});
/** Partial update for a reimbursement expense — all fields optional except type. */
export const UpdateReimbursementExpenseSchema = CreateReimbursementExpenseSchema.partial().extend({
    type: z.literal('REIMBURSEMENT'),
});
/** Partial update — same discriminated union shape with all fields optional except type. */
export const UpdateExpenseSchema = z.discriminatedUnion('type', [
    UpdateBusinessExpenseSchema,
    UpdateReimbursementExpenseSchema,
]);
// #endregion
// #region Response schema
export const ExpenseResponseSchema = z.object({
    id: z.string(),
    expenseNumber: z.string().nullable(),
    type: ExpenseTypeSchema,
    expenseDate: z.iso.datetime(),
    totalCost: z.number(),
    taxIncluded: z.boolean(),
    taxType: InvoiceTaxTypeSchema,
    taxRate: z.number(),
    taxAmount: z.number(),
    claimableAmountType: ClaimableTypeSchema,
    claimableAmount: z.number(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    // BUSINESS-only
    category: ExpenseCategorySchema.nullable(),
    lineItemId: z.string().nullable(),
    lineItemTitle: z.string().nullable(),
    claimed: z.boolean(),
    // REIMBURSEMENT-only
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    unitType: LineItemUnitTypeSchema,
    quantity: z.number(),
    // Billing — invoiceLineItemId set when attached to any invoice;
    // billed flips to true only when the invoice is sent
    billed: z.boolean(),
    invoiceLineItemId: z.string().nullable(),
    invoiceId: z.string().nullable(),
    // Recurring — set when generated from a template
    recurringExpenseId: z.string().nullable(),
    // Receipt presence (path is never returned; only signed URLs on demand)
    hasReceipt: z.boolean(),
    receiptMimeType: z.string().nullable(),
    receiptFilename: z.string().nullable(),
    receiptSizeBytes: z.number().nullable(),
    tags: z.array(TagResponseSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const ExpenseReceiptResponseSchema = z.object({
    receiptUrls: ImageVariantUrlsSchema,
    receiptFilename: z.string(),
    receiptMimeType: z.string(),
    receiptSizeBytes: z.number(),
});
/**
 * Signed URL map for viewing a stored receipt — `{ sm, xl }` for images, `{ xl }` for
 * PDFs.
 */
export const ExpenseReceiptUrlsResponseSchema = z.object({
    receiptUrls: ImageVariantUrlsSchema,
});
/** Request body for adding a reimbursement expense to an invoice. */
export const BillExpenseBodySchema = z.object({
    invoiceId: z.string().min(1, { error: 'Invoice is required' }),
});
// #endregion
// #region List query
export const ExpenseListQuerySchema = z.object({
    type: ExpenseTypeSchema.optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    category: ExpenseCategorySchema.optional(),
    clientId: z.string().optional(),
    tagIds: z
        .preprocess((val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]), z.array(z.string()))
        .optional(),
    q: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
// #endregion
//# sourceMappingURL=expenses.js.map